import cn from 'classnames';
import { useFormik } from 'formik';
import noop from 'lodash/noop';
import pick from 'lodash/pick';
import { useMemo } from 'react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import AdditionalSummaryForm from '../../components/forms/AdditionalSummaryForm/AdditionalSummaryForm';
import {
  DELAYED_SHIPPING_EXPIRATION,
  INITIAL_SUMMARY_FORM_STATE,
  INTERNAL_NOTES,
  SEND_ON_HOLD,
} from '../../constants/bucket';
import { BUSINESS_REASON_ID } from '../../constants/reasons';
import { USER } from '../../constants/users';
import { addBucketValue } from '../../store/actions/bucket';
import { selectAdminType, selectCurrentUserId } from '../../store/selectors/auth';
import { selectIsDelayedShipping, selectIsPYGEnabled, selectPYGCampaignsList } from '../../store/selectors/bucket';
import { selectDeliveryReportById } from '../../store/selectors/reports';
import { ISummaryFormFields, TEngagementCandidate } from '../../types/bucket';
import { IOneLink } from '../../types/oneLink';
import { IReport } from '../../types/reports';
import { IReportsRouteParams } from '../../types/routing';
import { isDigitalBox } from '../../utils/campaigns';
import { isDigitalReport, isNonEditableStatus, isValidScheduledEngagement } from '../../utils/reports';
import { hasPermission } from '../../utils/users';

import styles from './AdditionalSummaryContainer.module.scss';

interface IProps {
  className?: string;
  engagement: TEngagementCandidate | IReport | IOneLink | null | undefined;
  viewMode?: boolean;
  isOneLink?: boolean;
  sendId?: string;
  isSendEditable?: boolean;
}

const AdditionalSummaryContainer = ({ className, viewMode, engagement, isOneLink, sendId, isSendEditable }: IProps) => {
  const dispatch = useDispatch();
  const isPYGFlow = useSelector(selectIsPYGEnabled);
  const pygCampaigns = useSelector(selectPYGCampaignsList);
  const isDelayedShipping = useSelector(selectIsDelayedShipping);
  const { reportId, type } = useParams<IReportsRouteParams>();
  const adminType = useSelector(selectAdminType);
  const instance = useSelector(selectDeliveryReportById(reportId, type));
  const senderId = useMemo(() => instance?.user_id, [instance]);
  const currentUserId = useSelector(selectCurrentUserId);

  const isCurrentUsersSend = useMemo(() => senderId === currentUserId, [currentUserId, senderId]);
  const isUser = useMemo(() => hasPermission(adminType, USER), [adminType]);

  const canEditBusinessReasons = useMemo(() => {
    if (isNonEditableStatus(engagement as IReport)) {
      return false;
    }
    // Users cannot edit business reasons in OL
    if (isOneLink) {
      return !hasPermission([USER], adminType);
    }
    // in sends allow editing if not User OR the User created this send
    return !isUser || isCurrentUsersSend;
  }, [engagement, isOneLink, isUser, isCurrentUsersSend, adminType]);

  const canEditInternalNotes = useMemo((): boolean => {
    switch (true) {
      case isNonEditableStatus(engagement as IReport):
        return false;
      case isValidScheduledEngagement(engagement as IReport):
        return true;
      case isDigitalReport(engagement as IReport):
        return true;
      case isOneLink:
        return true;
      case isSendEditable:
        return true;
      default:
        return false;
    }
  }, [engagement, isOneLink, isSendEditable]);

  const formValues = React.useMemo(
    () => pick(engagement, [BUSINESS_REASON_ID, INTERNAL_NOTES, SEND_ON_HOLD, DELAYED_SHIPPING_EXPIRATION]),
    [engagement],
  );

  const form = useFormik<ISummaryFormFields>({
    initialValues: formValues ? formValues : INITIAL_SUMMARY_FORM_STATE,
    enableReinitialize: true,
    onSubmit: noop,
  });

  const handleFieldChange = React.useCallback(
    (name: keyof ISummaryFormFields, value: string | boolean) => {
      dispatch(addBucketValue({ [name]: value }));
    },
    [dispatch],
  );

  const isNotDigitalSend = React.useMemo(() => {
    if (isPYGFlow) {
      const items = pygCampaigns?.map((campaign) => campaign.items).flat();
      return !isDigitalBox({ items });
    }
    return !isDigitalBox(engagement);
  }, [engagement, isPYGFlow, pygCampaigns]);

  return (
    <div className={cn(className, styles.additionalSummaryContainer)}>
      <AdditionalSummaryForm
        form={form}
        onChange={handleFieldChange}
        readOnly={viewMode}
        onHoldIsAllowed={isNotDigitalSend}
        sendId={sendId}
        isDelayedShipping={isDelayedShipping}
        isEditBusinessReasonsAllowed={canEditBusinessReasons}
        isEditInternalNotesAllowed={canEditInternalNotes}
        internalNotes={engagement?.internal_notes}
      />
    </div>
  );
};

export default AdditionalSummaryContainer;
